<template>
  <div class="document-review-container">
    <div class="header">
      <h2>Document Review</h2>
      <div class="actions">
        <button class="primary-button" @click="saveChanges" :disabled="!hasChanges">Save Changes</button>
        <button class="secondary-button" @click="resetChanges" :disabled="!hasChanges">Reset</button>
      </div>
    </div>

    <div class="content-container">
      <!-- Document Preview Panel -->
      <div class="document-panel">
        <div class="panel-header">
          <h3>Document Preview</h3>
          <div class="document-controls">
            <div class="template-selector" v-if="documentType">
              <label>Template:</label>
              <select v-model="selectedTemplateId" @change="applyTemplate">
                <option value="">Auto-detect</option>
                <option v-for="template in availableTemplates" :key="template.id" :value="template.id">
                  {{ template.name }}
                </option>
              </select>
            </div>
            <div class="orientation-controls">
              <button @click="detectOrientation" title="Detect Orientation">
                <i class="fas fa-compass"></i>
              </button>
              <button @click="rotateLeft" title="Rotate Left">
                <i class="fas fa-undo"></i>
              </button>
              <button @click="rotateRight" title="Rotate Right">
                <i class="fas fa-redo"></i>
              </button>
            </div>
            <div class="zoom-controls">
              <button @click="zoomOut"><i class="fas fa-search-minus"></i></button>
              <span>{{ Math.round(zoomLevel * 100) }}%</span>
              <button @click="zoomIn"><i class="fas fa-search-plus"></i></button>
            </div>
          </div>
        </div>
        <div class="document-viewer" ref="documentViewer">
          <div 
            class="document-content" 
            :style="documentContentStyle"
            @mousedown="startSelection"
            @mousemove="updateSelection"
            @mouseup="endSelection"
          >
            <img 
              v-if="documentImage" 
              :src="documentImage" 
              alt="Document Preview" 
              @load="onImageLoad"
              ref="documentImageRef"
            />
            <div 
              v-for="(field, index) in highlightedFields" 
              :key="index" 
              class="field-highlight"
              :class="{ 'selected': field.id === selectedField?.id }"
              :style="getHighlightStyle(field)"
              @click.stop="selectField(field)"
            >
              <div class="field-label">{{ field.label }}</div>
            </div>
            <div 
              v-if="selectionActive" 
              class="selection-box"
              :style="selectionStyle"
            ></div>
          </div>
        </div>
      </div>

      <!-- Field Mapping Panel -->
      <div class="fields-panel">
        <div class="panel-header">
          <h3>Field Mapping</h3>
          <div class="filter-controls">
            <select v-model="confidenceFilter">
              <option value="all">All Fields</option>
              <option value="low">Low Confidence</option>
              <option value="medium">Medium Confidence</option>
              <option value="high">High Confidence</option>
            </select>
          </div>
        </div>
        <div class="fields-list">
          <div 
            v-for="field in filteredFields" 
            :key="field.id" 
            class="field-item"
            :class="{ 
              'selected': field.id === selectedField?.id,
              'low-confidence': field.confidence < 30,
              'medium-confidence': field.confidence >= 30 && field.confidence < 70,
              'high-confidence': field.confidence >= 70
            }"
            @click="selectField(field)"
          >
            <div class="field-header">
              <div class="field-name">{{ field.label }}</div>
              <div class="field-confidence">{{ field.confidence }}%</div>
            </div>
            <div class="field-value">
              <input 
                type="text" 
                v-model="field.value" 
                @input="onFieldValueChange(field)"
                :placeholder="field.placeholder || 'Enter value'"
              />
            </div>
            <div class="field-actions">
              <button 
                v-if="field.id === selectedField?.id" 
                class="map-button"
                @click="startFieldMapping(field)"
              >
                Map on Document
              </button>
              <button 
                v-if="field.id === selectedField?.id && field.originalValue !== field.value" 
                class="reset-button"
                @click="resetField(field)"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Audit Trail Panel -->
    <div class="audit-panel">
      <div class="panel-header">
        <h3>Audit Trail</h3>
        <button @click="toggleAuditPanel">{{ showAuditTrail ? 'Hide' : 'Show' }}</button>
      </div>
      <div v-if="showAuditTrail" class="audit-content">
        <div v-if="auditTrail.length === 0" class="no-changes">
          No changes have been made yet.
        </div>
        <div v-else class="audit-list">
          <div v-for="(entry, index) in auditTrail" :key="index" class="audit-entry">
            <div class="audit-timestamp">{{ formatTimestamp(entry.timestamp) }}</div>
            <div class="audit-message">{{ entry.message }}</div>
            <div class="audit-details">
              <div v-if="entry.oldValue !== undefined" class="audit-old-value">
                Old: {{ entry.oldValue || '(empty)' }}
              </div>
              <div v-if="entry.newValue !== undefined" class="audit-new-value">
                New: {{ entry.newValue || '(empty)' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import templateLibraryService from '@/services/templateLibraryService';
import documentOrientationService from '@/services/documentOrientationService';
import documentClassificationService from '@/services/documentClassificationService';

// Props
const props = defineProps({
  documentId: {
    type: String,
    required: true
  },
  documentImage: {
    type: String,
    required: true
  },
  extractedFields: {
    type: Array,
    required: true
  },
  documentType: {
    type: String,
    default: null
  }
});

// Emits
const emit = defineEmits(['save', 'field-updated']);

// State
const zoomLevel = ref(1.0);
const documentViewer = ref(null);
const documentImageRef = ref(null);
const availableTemplates = ref([]);
const selectedTemplateId = ref('');
const documentOrientation = ref({
  rotationAngle: 0,
  skewX: 0,
  skewY: 0,
  confidence: 100
});
const selectedField = ref(null);
const fields = ref([]);
const auditTrail = ref([]);
const showAuditTrail = ref(false);
const confidenceFilter = ref('all');
const hasChanges = ref(false);
const imageWidth = ref(0);
const imageHeight = ref(0);

// Selection state
const selectionActive = ref(false);
const selectionStart = ref({ x: 0, y: 0 });
const selectionEnd = ref({ x: 0, y: 0 });
const mappingField = ref(null);

// Initialize fields from props
onMounted(() => {
  fields.value = props.extractedFields.map(field => ({
    ...field,
    originalValue: field.value,
    modified: false
  }));
});

// Watch for changes in extractedFields prop
watch(() => props.extractedFields, (newFields) => {
  fields.value = newFields.map(field => ({
    ...field,
    originalValue: field.value,
    modified: false
  }));
}, { deep: true });

// Computed properties
const filteredFields = computed(() => {
  if (confidenceFilter.value === 'all') {
    return fields.value;
  } else if (confidenceFilter.value === 'low') {
    return fields.value.filter(field => field.confidence < 30);
  } else if (confidenceFilter.value === 'medium') {
    return fields.value.filter(field => field.confidence >= 30 && field.confidence < 70);
  } else {
    return fields.value.filter(field => field.confidence >= 70);
  }
});

const highlightedFields = computed(() => {
  return fields.value.filter(field => field.bounds);
});

const selectionStyle = computed(() => {
  if (!selectionActive.value) return {};
  
  const left = Math.min(selectionStart.value.x, selectionEnd.value.x);
  const top = Math.min(selectionStart.value.y, selectionEnd.value.y);
  const width = Math.abs(selectionEnd.value.x - selectionStart.value.x);
  const height = Math.abs(selectionEnd.value.y - selectionStart.value.y);
  
  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`
  };
});

const documentContentStyle = computed(() => {
  const orientationTransform = `
    rotate(${documentOrientation.value.rotationAngle}deg)
    skew(${documentOrientation.value.skewX}deg, ${documentOrientation.value.skewY}deg)
  `;
  
  return {
    transform: `scale(${zoomLevel.value}) ${orientationTransform}`,
    transformOrigin: 'top left'
  };
});

// Methods
function zoomIn() {
  zoomLevel.value = Math.min(zoomLevel.value + 0.1, 3.0);
}

function zoomOut() {
  zoomLevel.value = Math.max(zoomLevel.value - 0.1, 0.5);
}

function onImageLoad(event) {
  imageWidth.value = event.target.naturalWidth;
  imageHeight.value = event.target.naturalHeight;
  
  // Load templates for the document type if available
  if (props.documentType) {
    loadTemplates(props.documentType);
  }
  
  // Auto-detect orientation
  detectOrientation();
}

// Template handling methods
function loadTemplates(documentType) {
  availableTemplates.value = templateLibraryService.getTemplateVariations(documentType);
  
  if (availableTemplates.value.length > 0) {
    // Auto-detect the best template match
    autoDetectTemplate();
  }
}

function autoDetectTemplate() {
  if (!props.documentType || availableTemplates.value.length === 0) return;
  
  // In a real implementation, this would analyze the document content
  // For now, we'll use a mock implementation
  const mockTextBlocks = [
    { text: 'Application and Certificate for Payment', x: 100, y: 50 },
    { text: 'AIA Document G702', x: 400, y: 50 },
    { text: 'PROJECT:', x: 100, y: 100 },
    { text: 'APPLICATION NO:', x: 500, y: 100 }
  ];
  
  const result = templateLibraryService.identifyTemplateVariation(props.documentType, mockTextBlocks);
  
  if (result.template && result.confidence > 50) {
    selectedTemplateId.value = result.template.id;
    applyTemplate();
    
    // Add to audit trail
    addAuditEntry({
      message: `Auto-detected template: ${result.template.name} (${result.confidence}% confidence)`
    });
  }
}

function applyTemplate() {
  if (!selectedTemplateId.value || !props.documentType) return;
  
  const template = templateLibraryService.getTemplateVariation(props.documentType, selectedTemplateId.value);
  if (!template) return;
  
  // Map field positions based on template
  if (imageWidth.value && imageHeight.value) {
    const mappedFields = templateLibraryService.mapFieldPositions(template, imageWidth.value, imageHeight.value);
    
    // Update field bounds based on template
    fields.value.forEach(field => {
      const fieldName = Object.keys(mappedFields).find(name => 
        name.toLowerCase().replace(/_/g, ' ') === field.label.toLowerCase()
      );
      
      if (fieldName && mappedFields[fieldName]) {
        const oldBounds = field.bounds;
        field.bounds = mappedFields[fieldName];
        
        // Mark as modified if bounds changed
        if (JSON.stringify(oldBounds) !== JSON.stringify(field.bounds)) {
          field.modified = true;
          updateHasChanges();
          
          // Add to audit trail
          addAuditEntry({
            fieldId: field.id,
            fieldLabel: field.label,
            message: `Updated field position for "${field.label}" using template "${template.name}"`,
            oldBounds: oldBounds,
            newBounds: field.bounds
          });
        }
      }
    });
  }
}

// Orientation handling methods
function detectOrientation() {
  if (!documentImageRef.value) return;
  
  // In a real implementation, this would use the documentOrientationService
  // For now, we'll use a mock implementation
  const orientation = {
    rotationAngle: 0,
    skewX: 0,
    skewY: 0,
    confidence: 95
  };
  
  // Only update if significant changes are detected
  if (
    Math.abs(orientation.rotationAngle - documentOrientation.value.rotationAngle) > 1 ||
    Math.abs(orientation.skewX - documentOrientation.value.skewX) > 0.5 ||
    Math.abs(orientation.skewY - documentOrientation.value.skewY) > 0.5
  ) {
    const oldOrientation = { ...documentOrientation.value };
    documentOrientation.value = orientation;
    
    // Add to audit trail
    addAuditEntry({
      message: 'Auto-corrected document orientation',
      oldOrientation,
      newOrientation: orientation
    });
  }
}

function rotateLeft() {
  documentOrientation.value.rotationAngle -= 90;
  if (documentOrientation.value.rotationAngle < -360) {
    documentOrientation.value.rotationAngle += 360;
  }
  
  // Add to audit trail
  addAuditEntry({
    message: 'Rotated document left 90 degrees'
  });
}

function rotateRight() {
  documentOrientation.value.rotationAngle += 90;
  if (documentOrientation.value.rotationAngle > 360) {
    documentOrientation.value.rotationAngle -= 360;
  }
  
  // Add to audit trail
  addAuditEntry({
    message: 'Rotated document right 90 degrees'
  });
}

// Field handling methods
function selectField(field) {
  selectedField.value = field;
}

function onFieldValueChange(field) {
  field.modified = field.value !== field.originalValue;
  updateHasChanges();
  
  // Emit field updated event
  emit('field-updated', field);
}

function resetField(field) {
  const oldValue = field.value;
  field.value = field.originalValue;
  field.modified = false;
  updateHasChanges();
  
  // Add to audit trail
  addAuditEntry({
    fieldId: field.id,
    fieldLabel: field.label,
    message: `Reset value for field "${field.label}"`,
    oldValue,
    newValue: field.value
  });
}

function getHighlightStyle(field) {
  if (!field.bounds) return {};
  
  return {
    left: `${field.bounds.x}px`,
    top: `${field.bounds.y}px`,
    width: `${field.bounds.width}px`,
    height: `${field.bounds.height}px`
  };
}

// Selection and mapping methods
function startFieldMapping(field) {
  mappingField.value = field;
  document.body.style.cursor = 'crosshair';
}

function startSelection(event) {
  if (!mappingField.value) return;
  
  const rect = event.target.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  
  selectionStart.value = { x, y };
  selectionEnd.value = { x, y };
  selectionActive.value = true;
}

function updateSelection(event) {
  if (!selectionActive.value) return;
  
  const rect = event.target.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  
  selectionEnd.value = { x, y };
}

function endSelection() {
  if (!selectionActive.value || !mappingField.value) return;
  
  // Calculate bounds from selection
  const left = Math.min(selectionStart.value.x, selectionEnd.value.x);
  const top = Math.min(selectionStart.value.y, selectionEnd.value.y);
  const width = Math.abs(selectionEnd.value.x - selectionStart.value.x);
  const height = Math.abs(selectionEnd.value.y - selectionStart.value.y);
  
  // Only update if selection is large enough
  if (width > 10 && height > 10) {
    const oldBounds = mappingField.value.bounds;
    
    // Update field bounds
    mappingField.value.bounds = {
      x: left,
      y: top,
      width,
      height
    };
    
    // Add to audit trail
    addAuditEntry({
      fieldId: mappingField.value.id,
      fieldLabel: mappingField.value.label,
      message: `Updated mapping for field "${mappingField.value.label}"`,
      oldBounds: oldBounds,
      newBounds: mappingField.value.bounds
    });
    
    // Mark as modified
    mappingField.value.modified = true;
    updateHasChanges();
  }
  
  // Reset selection state
  selectionActive.value = false;
  mappingField.value = null;
  document.body.style.cursor = 'default';
}

// Audit trail methods
function addAuditEntry(entry) {
  auditTrail.value.push({
    ...entry,
    timestamp: new Date()
  });
}

function formatTimestamp(timestamp) {
  return timestamp.toLocaleTimeString();
}

function toggleAuditPanel() {
  showAuditTrail.value = !showAuditTrail.value;
}

// Save and reset methods
function updateHasChanges() {
  hasChanges.value = fields.value.some(field => field.modified);
}

function saveChanges() {
  // Prepare data for saving
  const changes = fields.value
    .filter(field => field.modified)
    .map(field => ({
      id: field.id,
      label: field.label,
      value: field.value,
      bounds: field.bounds
    }));
  
  // Emit save event with changes and audit trail
  emit('save', {
    changes,
    auditTrail: auditTrail.value
  });
  
  // Reset modified flags
  fields.value.forEach(field => {
    field.originalValue = field.value;
    field.modified = false;
  });
  
  // Clear audit trail
  auditTrail.value = [];
  
  // Update has changes flag
  updateHasChanges();
}

function resetChanges() {
  // Reset all fields to original values
  fields.value.forEach(field => {
    field.value = field.originalValue;
    field.modified = false;
  });
  
  // Clear audit trail
  auditTrail.value = [];
  
  // Update has changes flag
  updateHasChanges();
}
</script>

<style scoped>
.document-review-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #fff;
  border-bottom: 1px solid #e1e4e8;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #24292e;
}

.actions {
  display: flex;
  gap: 12px;
}

.primary-button, .secondary-button {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.primary-button {
  background-color: #0366d6;
  color: white;
  border: none;
}

.primary-button:hover {
  background-color: #0256b9;
}

.primary-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.secondary-button {
  background-color: #f6f8fa;
  color: #24292e;
  border: 1px solid #e1e4e8;
}

.secondary-button:hover {
  background-color: #e1e4e8;
}

.secondary-button:disabled {
  color: #959da5;
  cursor: not-allowed;
}

.content-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.document-panel, .fields-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.document-panel {
  flex: 2;
  border-right: 1px solid #e1e4e8;
}

.fields-panel {
  flex: 1;
  min-width: 300px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  color: #24292e;
}

.document-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}

.template-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.template-selector label {
  font-size: 14px;
  color: #24292e;
}

.template-selector select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e1e4e8;
  background-color: white;
}

.orientation-controls, .zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.orientation-controls button, .zoom-controls button {
  background: none;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
}

.orientation-controls button:hover, .zoom-controls button:hover {
  background-color: #f6f8fa;
}

.zoom-controls span {
  font-size: 14px;
  color: #24292e;
  min-width: 50px;
  text-align: center;
}

.document-viewer {
  flex: 1;
  overflow: auto;
  position: relative;
  background-color: #e1e4e8;
}

.document-content {
  position: relative;
  display: inline-block;
  margin: 20px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}

.document-content img {
  display: block;
  max-width: 100%;
}

.field-highlight {
  position: absolute;
  background-color: rgba(3, 102, 214, 0.1);
  border: 2px solid rgba(3, 102, 214, 0.5);
  border-radius: 2px;
  cursor: pointer;
}

.field-highlight.selected {
  background-color: rgba(3, 102, 214, 0.2);
  border-color: #0366d6;
}

.field-highlight .field-label {
  position: absolute;
  top: -20px;
  left: 0;
  background-color: #0366d6;
  color: white;
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 12px;
  white-space: nowrap;
}

.selection-box {
  position: absolute;
  border: 2px dashed #0366d6;
  background-color: rgba(3, 102, 214, 0.1);
  pointer-events: none;
}

.fields-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.field-item {
  margin-bottom: 12px;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  background-color: white;
  overflow: hidden;
}

.field-item.selected {
  border-color: #0366d6;
  box-shadow: 0 0 0 1px #0366d6;
}

.field-item.low-confidence {
  border-left: 4px solid #d73a49;
}

.field-item.medium-confidence {
  border-left: 4px solid #f9c513;
}

.field-item.high-confidence {
  border-left: 4px solid #28a745;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
}

.field-name {
  font-weight: 500;
  color: #24292e;
}

.field-confidence {
  font-size: 12px;
  color: #586069;
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #e1e4e8;
}

.field-value {
  padding: 8px 12px;
}

.field-value input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
}

.field-value input:focus {
  border-color: #0366d6;
  outline: none;
  box-shadow: 0 0 0 1px #0366d6;
}

.field-actions {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  gap: 8px;
  border-top: 1px solid #e1e4e8;
}

.map-button, .reset-button {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.map-button {
  background-color: #0366d6;
  color: white;
  border: none;
}

.map-button:hover {
  background-color: #0256b9;
}

.reset-button {
  background-color: #f6f8fa;
  color: #24292e;
  border: 1px solid #e1e4e8;
}

.reset-button:hover {
  background-color: #e1e4e8;
}

.filter-controls {
  display: flex;
  align-items: center;
}

.filter-controls select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e1e4e8;
  background-color: white;
}

.audit-panel {
  border-top: 1px solid #e1e4e8;
  background-color: white;
}

.audit-content {
  max-height: 200px;
  overflow-y: auto;
  padding: 12px;
}

.no-changes {
  color: #586069;
  text-align: center;
  padding: 16px;
}

.audit-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audit-entry {
  padding: 8px;
  border: 1px solid #e1e4e8;
  border-radius: 4px;
  background-color: #f6f8fa;
}

.audit-timestamp {
  font-size: 12px;
  color: #586069;
  margin-bottom: 4px;
}

.audit-message {
  margin-bottom: 4px;
}

.audit-details {
  font-size: 12px;
  color: #586069;
}

.audit-old-value, .audit-new-value {
  margin-top: 2px;
}
</style>